
import React, { useState, useEffect } from 'react';
import { ShieldCheck, Search, CheckCircle2, XCircle, Clock, RefreshCw } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useKYC } from '@/shared/hooks/useKYC';

// 유형 한글 변환
const KYC_TYPE_LABELS: Record<string, string> = {
    ID_CARD: '신분증',
    PASSPORT: '여권',
    DRIVER_LICENSE: '운전면허증',
    BUSINESS: '사업자등록증',
    ADDRESS: '주소 증명',
    CREDIT_CARD: '신용카드',
    BANK_ACCOUNT: '계좌 인증',
    SELFIE: '셀피',
    PHONE: '휴대폰 인증',
};

// 상태 한글 변환
const STATUS_LABELS: Record<string, string> = {
    PENDING: '대기중',
    Pending: '대기중',
    Approved: '승인됨',
    APPROVED: '승인됨',
    Rejected: '거절됨',
    REJECTED: '거절됨',
};

const AdminVerificationPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState('pending');
    const { getAllApplications, processApplication } = useKYC();
    const [requests, setRequests] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Rejection modal state
    const [rejectModal, setRejectModal] = useState<{ appId: string; userId: string; type: string } | null>(null);
    const [rejectionReason, setRejectionReason] = useState('');

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        setLoading(true);
        const data = await getAllApplications();
        setRequests(data);
        setLoading(false);
    };

    const handleApprove = async (id: string, userId: string, type: string) => {
        if (!confirm('정말로 이 요청을 승인하시겠습니까?')) return;
        const result = await processApplication(id, userId, type, 'Approved');
        if (result.success) {
            alert('요청이 승인되었습니다.');
            fetchRequests();
        } else {
            alert('처리에 실패했습니다.');
        }
    };

    const handleRejectSubmit = async () => {
        if (!rejectModal) return;
        if (!rejectionReason.trim()) { alert('거절 사유를 입력해주세요.'); return; }
        const result = await processApplication(rejectModal.appId, rejectModal.userId, rejectModal.type, 'Rejected', rejectionReason.trim());
        if (result.success) {
            alert('거절 처리가 완료되었습니다.');
            setRejectModal(null);
            setRejectionReason('');
            fetchRequests();
        } else {
            alert('처리에 실패했습니다.');
        }
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
    };

    const [viewingImage, setViewingImage] = useState<string | null>(null);

    const getTypeLabel = (type: string) => KYC_TYPE_LABELS[type] || type;
    const getStatusLabel = (status: string) => STATUS_LABELS[status] || status;

    const filteredRequests = requests
        .filter(r => (r.status || 'Pending').toLowerCase() === activeTab)
        .filter(r => {
            if (!searchQuery.trim()) return true;
            const q = searchQuery.toLowerCase();
            return (
                (r.userName || r.name || r.userId || '').toLowerCase().includes(q) ||
                getTypeLabel(r.type).includes(searchQuery)
            );
        });

    return (
        <div className="space-y-8 animate-in fade-in duration-700">
            {/* Rejection Reason Modal */}
            {rejectModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => { setRejectModal(null); setRejectionReason(''); }}>
                    <div className="bg-[#0f0f12] border border-white/10 rounded-3xl p-8 w-full max-w-lg space-y-6 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                        <h2 className="text-xl font-black text-white">거절 사유 입력</h2>
                        <p className="text-sm text-slate-500 font-bold">입력하신 사유는 사용자 패널에 표시됩니다.</p>
                        <textarea
                            value={rejectionReason}
                            onChange={e => setRejectionReason(e.target.value)}
                            placeholder="예) 제출하신 신분증 사진이 불명확합니다. 다시 선명하게 촬영하여 제출해주세요."
                            rows={5}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-sm font-bold text-white outline-none focus:border-red-500/50 resize-none transition-all"
                        />
                        <div className="flex gap-3">
                            <button onClick={() => { setRejectModal(null); setRejectionReason(''); }} className="flex-1 h-13 rounded-2xl bg-slate-800 text-slate-400 font-black text-base hover:bg-slate-700 transition-all">취소</button>
                            <button onClick={handleRejectSubmit} className="flex-1 h-13 rounded-2xl bg-red-600 text-white font-black text-base hover:bg-red-500 transition-all">거절 확정</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Viewer Modal */}
            {viewingImage && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setViewingImage(null)}>
                    <div className="relative max-w-4xl w-full max-h-[90vh] flex flex-col items-center justify-center p-2 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setViewingImage(null)}
                            className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
                        >
                            <XCircle className="h-8 w-8" />
                        </button>
                        <img
                            src={viewingImage}
                            alt="Verification Document"
                            className="max-w-full max-h-[85vh] rounded-lg shadow-2xl border border-white/10 object-contain bg-[#0f0f12]"
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://placehold.co/600x400/png?text=Document+Preview';
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <h1 className="text-3xl font-black flex items-center gap-3">
                        <ShieldCheck className="h-8 w-8 text-blue-500" /> 본인인증 관리
                    </h1>
                    <p className="text-slate-500 font-bold text-sm uppercase tracking-widest">본인인증 승인 센터</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={fetchRequests}
                        className="h-12 px-5 rounded-xl bg-slate-950 border border-slate-800 flex items-center gap-2 text-slate-400 hover:text-white transition-all text-sm font-bold"
                    >
                        <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} /> 새로고침
                    </button>
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="사용자 검색..."
                            className="h-12 w-72 bg-slate-950 border border-slate-800 rounded-xl pl-11 pr-4 text-sm font-bold outline-none focus:border-blue-500/50 transition-all"
                        />
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-slate-950 border border-slate-800 rounded-2xl w-fit">
                {['pending', 'approved', 'rejected'].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={cn(
                            "px-8 py-3 rounded-xl text-sm font-black tracking-wide transition-all",
                            activeTab === tab ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20" : "text-slate-500 hover:text-white"
                        )}
                    >
                        {tab === 'pending' ? '대기' : tab === 'approved' ? '승인' : '거절'} ({requests.filter(r => (r.status || 'Pending').toLowerCase() === tab).length})
                    </button>
                ))}
            </div>

            {/* Table */}
            <div className="rounded-[2rem] bg-slate-950 border border-slate-800 overflow-hidden shadow-xl">
                <table className="w-full text-sm">
                    <thead className="bg-[#0f0f12] text-slate-400 font-black uppercase tracking-widest border-b border-slate-800">
                        <tr>
                            <th className="px-7 py-5 text-left text-sm">날짜</th>
                            <th className="px-7 py-5 text-left text-sm">사용자 이름</th>
                            <th className="px-7 py-5 text-left text-sm">유형</th>
                            <th className="px-7 py-5 text-left text-sm">상세정보</th>
                            <th className="px-7 py-5 text-center text-sm">상태</th>
                            <th className="px-7 py-5 text-right text-sm">관리</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800 font-medium">
                        {filteredRequests.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-7 py-16 text-center text-slate-600 text-base font-bold">
                                    {loading ? '불러오는 중...' : '해당 항목이 없습니다.'}
                                </td>
                            </tr>
                        )}
                        {filteredRequests.map((req) => (
                            <tr key={req.id} className="hover:bg-white/[0.02] group transition-colors">
                                <td className="px-7 py-6 text-slate-400 font-mono text-sm whitespace-nowrap">{formatDate(req.createdAt)}</td>
                                <td className="px-7 py-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-sm font-black text-white">
                                            {(req.userName || req.name || req.userId || '?')[0].toUpperCase()}
                                        </div>
                                        <span className="font-bold text-white text-base group-hover:text-blue-400 transition-colors">
                                            {req.userName || req.name || req.userId}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-7 py-6">
                                    <span className="px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-300 text-sm font-black border border-blue-500/20">
                                        {getTypeLabel(req.type)}
                                    </span>
                                </td>
                                <td className="px-7 py-6">
                                    <div className="text-sm text-slate-400 space-y-1">
                                        {/* 전화 */}
                                        {req.data?.phoneNumber && <div>📱 {req.data.phoneNumber}</div>}
                                        {/* 계좌 */}
                                        {req.data?.bankName && <div>🏦 {req.data.bankName} {req.data.accountNumber}</div>}
                                        {/* 신용카드 */}
                                        {req.data?.cardNumber && <div>💳 {req.data.cardNumber}</div>}
                                        {req.data?.cardHolderName && <div>👤 {req.data.cardHolderName}</div>}
                                        {req.data?.expiryMonth && <div>📅 유효: {req.data.expiryMonth}/{req.data.expiryYear}</div>}
                                        {req.data?.cvc && <div>🔒 CVC: {req.data.cvc}</div>}
                                        {/* 구버전 호환 */}
                                        {!req.data?.cardNumber && req.data?.cardNumberLast4 && <div>💳 **** **** **** {req.data.cardNumberLast4}</div>}
                                        {/* 파일 */}
                                        {req.data?.fileName && (
                                            <div
                                                className="text-blue-400 underline cursor-pointer hover:text-blue-300 transition-colors"
                                                onClick={() => {
                                                    const url = req.data.imageUrl || req.data.mockImageUrl;
                                                    if (url) {
                                                        setViewingImage(url);
                                                    } else {
                                                        alert('이미지 URL이 없습니다.');
                                                    }
                                                }}
                                            >
                                                📄 파일 보기: {req.data.fileName}
                                            </div>
                                        )}
                                        {/* 주소 */}
                                        {req.data?.address && <div>🏠 {req.data.address}</div>}
                                        {/* 사업자 */}
                                        {req.data?.companyName && <div>🏢 {req.data.companyName} ({req.data.registrationNumber})</div>}
                                        {/* 아무것도 없으면 raw JSON */}
                                        {!req.data?.phoneNumber && !req.data?.bankName && !req.data?.cardNumberLast4 && !req.data?.fileName && !req.data?.address && !req.data?.companyName && req.data && (
                                            <div className="text-slate-600 font-mono text-xs">{JSON.stringify(req.data)}</div>
                                        )}
                                    </div>
                                </td>
                                <td className="px-7 py-6">
                                    <div className="flex justify-center">
                                        <div className={cn(
                                            "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-black",
                                            (req.status === 'PENDING' || req.status === 'Pending' || !req.status)
                                                ? "bg-amber-500/10 text-amber-400"
                                                : (req.status === 'Approved' || req.status === 'APPROVED')
                                                    ? "bg-emerald-500/10 text-emerald-400"
                                                    : "bg-red-500/10 text-red-400"
                                        )}>
                                            {(req.status === 'PENDING' || req.status === 'Pending' || !req.status) && <Clock className="h-4 w-4" />}
                                            {(req.status === 'Approved' || req.status === 'APPROVED') && <CheckCircle2 className="h-4 w-4" />}
                                            {(req.status === 'Rejected' || req.status === 'REJECTED') && <XCircle className="h-4 w-4" />}
                                            {getStatusLabel(req.status || 'Pending')}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-7 py-6 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        {(req.status === 'PENDING' || req.status === 'Pending' || !req.status) && (
                                            <>
                                                <button
                                                    onClick={() => handleApprove(req.id, req.userId, req.type)}
                                                    className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-black hover:bg-emerald-500 transition-all"
                                                >
                                                    승인
                                                </button>
                                                <button
                                                    onClick={() => setRejectModal({ appId: req.id, userId: req.userId, type: req.type })}
                                                    className="px-4 py-2 rounded-lg bg-red-600/10 border border-red-600/20 text-red-400 text-sm font-black hover:bg-red-600 hover:text-white transition-all"
                                                >
                                                    거절
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminVerificationPage;
